import React, { useEffect, useRef, useState } from 'react'
import { MenuProps } from 'components/menu'
import FloatingMenu from 'components/menu/floating'
import { MenuItem, MenuList, MenuSearch } from 'components/menu/items'
import { PanelAction, PanelFooter } from 'components/menu/panel'
import Toggle from 'components/toggle'
import { Asterisk, Cross } from 'components/icons'
import { matchesAllTokens } from 'helpers/search'
import { IColumnTypes } from 'components/spreadsheet/types'
import IconSelector from 'components/spreadsheet/components/cell/components/icon'

// Which of a form's fields must be filled in, as Hide columns lists which show: a search, then each column as
// a menu row with its type's icon, its name and a switch (on while it's required), and Require all / Unrequire
// all (of those the search finds) at the foot. A column Morta calculates is listed with its switch off-limits and
// the reason as a tooltip.

interface RequiredMenuProps extends MenuProps {
  columns: Array<{ publicId: string; name: string; kind: IColumnTypes; required?: boolean }>
  /** Why a column can't be required, or undefined when it can. */
  cantRequireReason: (columnId: string) => string | undefined
  onRequire: (columnId: string) => void
  onUnrequire: (columnId: string) => void
}

const RequiredMenu: React.FC<RequiredMenuProps> = ({
  id,
  menuState,
  setMenuState,
  width,
  columns,
  cantRequireReason,
  onRequire,
  onUnrequire
}) => {
  const [search, setSearch] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  // Ready to type a column's name as soon as it opens, as Hide columns is.
  useEffect(() => searchRef.current?.focus(), [])
  const shown = columns.filter((column) => matchesAllTokens({ label: column.name, value: column.publicId }, search))
  // A column that can't be required can still be made optional, so a calculated column that's already required
  // (from before it was calculated, say) isn't stuck.
  const reasonFor = (column: { publicId: string; required?: boolean }) =>
    column.required ? undefined : cantRequireReason(column.publicId)
  const requirable = shown.filter((column) => !column.required && !cantRequireReason(column.publicId))

  return (
    <FloatingMenu
      id={id}
      menuState={menuState}
      setMenuState={setMenuState}
      width={width}
      zIndex={3000}
      title="Required fields"
    >
      <MenuSearch
        ref={searchRef}
        placeholder="Search columns"
        aria-label="Search columns"
        onChange={(event) => setSearch(event.target.value)}
      />
      <MenuList className="max-h-[320px] overflow-y-auto">
        {shown.length === 0 && <div className="px-2 py-1.5 text-[13px] text-muted select-none">No columns found</div>}
        {shown.map((column) => {
          const reason = reasonFor(column)
          const isRequired = !!column.required
          const flip = (require: boolean) => (require ? onRequire(column.publicId) : onUnrequire(column.publicId))
          return (
            <MenuItem
              key={column.publicId}
              data-testid="required-column-row"
              icon={<IconSelector kind={column.kind} />}
              disabledReason={reason}
              onClick={() => flip(!isRequired)}
              end={
                <Toggle
                  aria-label={`Require ${column.name}`}
                  toggled={isRequired}
                  onToggle={flip}
                  disabled={!!reason}
                />
              }
            >
              {column.name}
            </MenuItem>
          )
        })}
      </MenuList>
      <PanelFooter>
        {/* All of those the search shows, as a list's all-at-once buttons go. */}
        <PanelAction icon={<Asterisk />} onClick={() => requirable.forEach((c) => onRequire(c.publicId))}>
          Require all
        </PanelAction>
        <PanelAction
          icon={<Cross />}
          onClick={() => shown.filter((column) => column.required).forEach((c) => onUnrequire(c.publicId))}
        >
          Unrequire all
        </PanelAction>
      </PanelFooter>
    </FloatingMenu>
  )
}

export default RequiredMenu
